import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, Sparkles, Wand2, CheckCircle2, Info, Download } from "lucide-react";
import { getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdaptCVModal({ jobId, jobTitle, companyName, onClose, onSaved }) {
  const { t } = useLanguage();
  const [state, setState] = useState("loading"); // loading | ready | error | saving | saved
  const [preview, setPreview] = useState(null);
  const [versionName, setVersionName] = useState("");
  const [savedVersion, setSavedVersion] = useState(null);

  useEffect(() => {
    let active = true;
    axios.post(`${API}/jobs/${jobId}/adapt-cv/preview`, {}, { headers: getAuthHeaders() })
      .then((res) => {
        if (!active) return;
        setPreview(res.data);
        setVersionName(`${t("cvAdapt.defaultVersionPrefix")} ${res.data.job_title}`);
        setState("ready");
      })
      .catch(() => { if (active) setState("error"); });
    return () => { active = false; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    setState("saving");
    try {
      const res = await axios.post(`${API}/cv-versions`, {
        job_id: jobId,
        version_name: versionName,
        adapted_cv: preview.adapted_cv,
        suggested_additions: preview.suggested_additions,
      }, { headers: getAuthHeaders() });
      setSavedVersion(res.data);
      setState("saved");
      if (onSaved) onSaved(res.data);
    } catch {
      setState("ready");
    }
  };

  const downloadPdf = async (versionId, name) => {
    const res = await axios.get(`${API}/cv-versions/${versionId}/download-pdf`, { headers: getAuthHeaders(), responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(name || "CV").replace(/\s+/g, "_")}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="adapt-cv-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#F55FA5]" />
            <h3 className="font-poppins font-bold text-lg text-slate-900">{t("cvAdapt.modalTitle")}</h3>
          </div>
          <button onClick={onClose} data-testid="adapt-cv-close-btn" className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {state === "loading" && (
          <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="adapt-cv-loading">
            <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
            <p className="text-sm text-slate-500">{t("cvAdapt.generating")}</p>
          </div>
        )}

        {state === "error" && (
          <div className="text-center py-12" data-testid="adapt-cv-error">
            <p className="text-sm text-slate-500 mb-4">{t("cvAdapt.errorMsg")}</p>
            <button onClick={onClose} className="text-sm font-semibold text-[#028CF8]">{t("cvAdapt.close")}</button>
          </div>
        )}

        {(state === "ready" || state === "saving") && preview && (
          <div data-testid="adapt-cv-preview">
            <p className="text-xs text-slate-600 mb-5">
              {t("cvAdapt.forJob")} <span className="font-semibold text-slate-800">{preview.job_title || jobTitle}</span> · {preview.company_name || companyName}
            </p>

            <div className="mb-5" data-testid="adapt-cv-summary-section">
              <p className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">{t("cvAdapt.summaryTitle")}</p>
              <div className="bg-slate-50 rounded-xl p-3 mb-2">
                <p className="text-[10px] font-semibold text-slate-600 uppercase mb-1">{t("cvAdapt.original")}</p>
                <p className="text-xs text-slate-500" data-testid="adapt-cv-summary-original">{preview.original_cv?.summary || "—"}</p>
              </div>
              <div className="bg-[#028CF8]/5 border border-[#028CF8]/20 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-[#028CF8] uppercase mb-1">{t("cvAdapt.adapted")}</p>
                <p className="text-xs text-slate-700" data-testid="adapt-cv-summary-adapted">{preview.adapted_cv?.summary || "—"}</p>
              </div>
            </div>

            <div className="mb-5" data-testid="adapt-cv-skills-section">
              <p className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">{t("cvAdapt.skillsTitle")}</p>
              <div className="flex flex-wrap gap-1.5" data-testid="adapt-cv-skills-list">
                {(preview.adapted_cv?.skills || []).map((s, i) => (
                  <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#028CF8]/10 text-[#028CF8]">{s}</span>
                ))}
              </div>
            </div>

            <div className="mb-5" data-testid="adapt-cv-experience-section">
              <p className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-2">{t("cvAdapt.experienceTitle")}</p>
              <div className="space-y-2" data-testid="adapt-cv-experience-list">
                {(preview.adapted_cv?.experience || []).map((e, i) => (
                  <div key={i} className="border border-slate-100 rounded-xl p-3">
                    <p className="text-xs font-semibold text-slate-900">{e.position} · {e.company}</p>
                    <p className="text-[10px] text-slate-400 mb-1">{e.start_date} – {e.current ? t("cv.currentJob") : e.end_date}</p>
                    {e.description && <p className="text-xs text-slate-600">{e.description}</p>}
                  </div>
                ))}
              </div>
            </div>

            {preview.suggested_additions?.length > 0 && (
              <div className="mb-5 bg-[#F55FA5]/5 border border-dashed border-[#F55FA5]/30 rounded-xl p-3.5" data-testid="adapt-cv-suggestions-section">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Info className="h-3.5 w-3.5 text-[#F55FA5]" />
                  <p className="text-xs font-bold text-slate-900">{t("cvAdapt.suggestionsTitle")}</p>
                </div>
                <p className="text-xs text-slate-500 mb-2">{t("cvAdapt.suggestionsHint")}</p>
                <div className="flex flex-wrap gap-1.5" data-testid="adapt-cv-suggestions-list">
                  {preview.suggested_additions.map((s, i) => (
                    <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F55FA5]/10 text-[#F55FA5]">{s}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-slate-100">
              <label className="text-xs font-semibold text-slate-500 mb-1.5 block">{t("cvAdapt.versionNameLabel")}</label>
              <input value={versionName} onChange={(e) => setVersionName(e.target.value)} data-testid="adapt-cv-version-name-input"
                className="w-full h-10 px-3 border border-slate-200 rounded-xl text-sm mb-4 focus:outline-none focus:border-[#028CF8]" />
              <div className="flex items-center gap-3">
                <button onClick={onClose} data-testid="adapt-cv-discard-btn"
                  className="flex-1 h-11 border border-slate-200 text-slate-600 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-all">
                  {t("cvAdapt.discard")}
                </button>
                <button onClick={handleSave} disabled={state === "saving"} data-testid="adapt-cv-save-btn"
                  className="flex-1 h-11 bg-[#028CF8] text-white text-sm font-semibold rounded-xl hover:bg-[#0270C5] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                  {state === "saving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {t("cvAdapt.saveVersion")}
                </button>
              </div>
            </div>
          </div>
        )}

        {state === "saved" && savedVersion && (
          <div className="text-center py-8" data-testid="adapt-cv-success">
            <CheckCircle2 className="h-12 w-12 text-[#10B981] mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-900 mb-1">{t("cvAdapt.savedTitle")}</p>
            <p className="text-xs text-slate-500 mb-5">{savedVersion.version_name}</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => downloadPdf(savedVersion.version_id, savedVersion.version_name)} data-testid="adapt-cv-download-pdf-link"
                className="h-11 px-5 flex items-center gap-2 border border-[#028CF8]/30 text-[#028CF8] text-sm font-semibold rounded-xl hover:bg-[#028CF8]/5 transition-all">
                <Download className="h-4 w-4" /> {t("cvAdapt.downloadPdf")}
              </button>
              <button onClick={onClose} data-testid="adapt-cv-finish-btn"
                className="h-11 px-5 bg-[#028CF8] text-white text-sm font-semibold rounded-xl hover:bg-[#0270C5] transition-all">
                {t("cvAdapt.close")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
